import { useMemo, useState } from "react";
import { Check, ChevronRight, Loader2, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";
import type { KaiToolActivity } from "./kaiTypes";
import { collectToolDownloads } from "./kaiToolDownloads";
import { KaiDownloads } from "./KaiDownloads";
import { KaiBlDocumentsCard } from "./KaiBlDocumentsCard";
import { KaiGraphMailAttachmentsCard } from "./KaiGraphMailAttachmentsCard";
import { KaiRateSourceCard } from "./KaiRateSourceCard";

interface KaiToolGroupProps {
  tools: KaiToolActivity[];
  live?: boolean;
  statusMessage?: string;
}

/** Collapsible group of MCP tool calls for a Kai turn. */
export function KaiToolGroup({
  tools,
  live = false,
  statusMessage,
}: KaiToolGroupProps) {
  const [expanded, setExpanded] = useState(live);
  const {
    fileRefs,
    reportRefs,
    blDocuments,
    graphMailAttachments,
    rateSourceAttachments,
  } = useMemo(() => collectToolDownloads(tools), [tools]);

  const allDone = tools.every((t) => t.status !== "running");
  const hasErrors = tools.some((t) => t.status === "error");
  const toolCount = tools.length;
  const summaryText =
    toolCount === 1
      ? `Used 1 tool: ${tools[0].name}`
      : `Used ${toolCount} tools`;

  return (
    <div className="w-full max-w-[min(100%,38rem)]">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 rounded-xl px-1 py-1 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 transition-transform",
            expanded && "rotate-90",
          )}
        />
        {!allDone ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
        ) : hasErrors ? (
          <TriangleAlert className="size-3.5 shrink-0 text-amber-500" />
        ) : (
          <Check className="size-3.5 shrink-0 text-emerald-500" />
        )}
        <span className="truncate">{summaryText}</span>
      </button>

      {expanded && (
        <div className="mt-1 space-y-1 pl-6">
          {tools.map((tool, idx) => (
            <div key={`${tool.name}-${idx}`}>
              <div className="flex items-center gap-2 text-xs">
                {tool.status === "running" ? (
                  <Loader2 className="size-3 shrink-0 animate-spin text-primary" />
                ) : tool.status === "error" ? (
                  <TriangleAlert className="size-3 shrink-0 text-destructive" />
                ) : (
                  <Check className="size-3 shrink-0 text-emerald-500" />
                )}
                <span className="font-medium text-foreground/80">
                  {tool.name}
                </span>
                {tool.args && (
                  <span className="truncate text-muted-foreground">
                    {tool.args}
                  </span>
                )}
              </div>
              {tool.status === "error" && tool.errorMessage && (
                <pre className="mt-1 overflow-x-auto rounded-lg bg-destructive/8 px-2 py-1.5 font-mono text-[11px] text-destructive">
                  {tool.errorMessage}
                </pre>
              )}
            </div>
          ))}
          {statusMessage && (
            <p className="pt-0.5 text-xs text-muted-foreground">
              {statusMessage}
            </p>
          )}
        </div>
      )}

      {blDocuments.length > 0 && (
        <div className="mt-1.5 pl-6">
          <KaiBlDocumentsCard documents={blDocuments} />
        </div>
      )}

      {graphMailAttachments.length > 0 && (
        <div className="mt-1.5 pl-6">
          <KaiGraphMailAttachmentsCard attachments={graphMailAttachments} />
        </div>
      )}

      {rateSourceAttachments.length > 0 && (
        <div className="mt-1.5 pl-6">
          <KaiRateSourceCard attachments={rateSourceAttachments} />
        </div>
      )}

      {(fileRefs.length > 0 || reportRefs.length > 0) && (
        <div className="mt-1.5 pl-6">
          <KaiDownloads fileRefs={fileRefs} reportRefs={reportRefs} />
        </div>
      )}
    </div>
  );
}
